import * as React from 'react';
import { Button, Dialog, Typography } from '@mui/material';
import { Data } from './SortTable';
import Title from '../Dialogs/Title';
import { useCancelContract } from '../../../hooks/contract.hook';
import ButtonLoader from '../../../utils/ButtonLoader';
import { formatCurrency } from '../../../utils/FormatCurrency';
import dayjs from 'dayjs';

interface IProps {
    config: any;
    data: Data;
    onSuccess: () => void;
}

export function CancelButton({config, data, onSuccess}: IProps) {
    const [cancelModalOpen, setCancelModalOpen] = React.useState(false);
	const {isMutating, response, trigger} = useCancelContract(config);

    const handleClose = () => {
        if (isMutating) return;
        setCancelModalOpen(false);
        if (response?.success) {
            onSuccess();
        }
    }

    if (!data.memberCancellationData.memberCanCancel) {
        return null;
    }

    return (
        <>
            <Button
                disabled={cancelModalOpen}
                color="error"
                fullWidth
                sx={{ bgcolor: "#FAFAFA", whiteSpace: "nowrap" }}
                variant="outlined"
                onClick={() => setCancelModalOpen(true)}
            >
                Cancel
            </Button>

            <Dialog
                aria-labelledby="cancel-dialog-title"
                aria-describedby="cancel-dialog-description"
                open={cancelModalOpen}
                onClose={handleClose}
            >
                {response?.success
                    ? <SuccessContent
                        clubData={config.clubData}
                        contractEndingDate={response.contractEndingDate} // 2025-06-11T00:00:00Z
                        dueAmount={data.dueAmount}
                        onClose={handleClose}
                    />
                    : <ConfirmContent
                        anticipatedCancellationDate={data.memberCancellationData.anticipatedCancellationDate.slice(0, 10)}
                        error={response?.errorMessage}
                        isMutating={isMutating}
                        onClose={handleClose}
                        onConfirm={() => trigger(data.id.toString())}
                    />
                }
            </Dialog>
        </>
    );
}

function ConfirmContent(props: any) {
    return (
        <>
            <Title title="Cancellation Confirmation" onClose={props.onClose} />
            <div style={{ padding: 24 }}>
                <Typography gutterBottom variant="body1">
                    You are about to cancel your contract. Please note:
                </Typography>

                <ul>
                    <li>
                        {props.anticipatedCancellationDate
                            ? `Anticipated Cancellation Date: ${dayjs(props.anticipatedCancellationDate).format("MM/DD/YYYY")};`
                            : 'This cancellation will be effective immediately;'
                        }
                    </li>
                    <li>
                        Pursuant to the terms of your contract, you will still be responsible for any outstanding balance or fees.
                    </li>
                </ul>

                <Typography gutterBottom variant="body1" marginTop={4} fontWeight={600}>
                    By clicking “Yes” you acknowledge that:
                </Typography>

                <ul>
                    <li>
                        You have reviewed the cancellation terms of your contract;
                    </li>
                    <li>
                        You understand and agree that any remaining amount due under the contract will still be owed and may be charged in accordance with the agreed terms.
                    </li>
                </ul>


                {props.error && (
                    <Typography color="error" gutterBottom variant="body2" sx={{ mt: 2 }}>
                        {props.error || 'An error occurred while processing your request.'}
                    </Typography>
                )}
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button
                        color="primary"
                        disabled={props.isMutating}
                        variant="outlined"
                        onClick={props.onClose}
                    >
                        No
                    </Button>
                    <Button
                        color="error"
                        disabled={props.isMutating}
                        variant="contained"
                        onClick={props.onConfirm}
                    >
                        { props.isMutating ? <ButtonLoader /> : 'Yes'}
                    </Button>
                </div>
            </div>
        </>
    )
}

function SuccessContent(props: any) {
    return (
        <>
            <Title title="Contract Cancellation Successful" onClose={props.onClose} />
            <div style={{ padding: 24 }}>

                <ul>
                    <li>
                        Amount Due {formatCurrency(props.dueAmount, props.clubData)} through {dayjs.utc(props.contractEndingDate).format("MM/DD/YYYY")}. Please note that if additional payments are due per contract terms, those will be reflected in upcoming invoices as they are created.
                    </li>
                    <li>
                        Please review your contract and terms for any additional payments.
                    </li>
                </ul>

                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={props.onClose}
                    >
                        Close
                    </Button>
                </div>
            </div>
        </>
    )
}
